import { useEffect, useState } from 'react';
import {
  fetchSpiritualMeetings,
  fetchCoachMeetings,
  saveCoachMeeting,
  registerSpiritualMeeting,
  attendSpiritualMeeting,
  downloadMeetingAttendanceCsv,
  canConfigureSpiritualContent,
} from '../../../utils/spiritualApi';

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const emptyForm = () => ({
  id: null,
  title: '',
  start_time: '',
  end_time: '',
  zoom_link: '',
  recording_url: '',
  capacity: '',
  description: '',
});

export default function SpiritualMeetingsPanel({ user }) {
  const isCoach = canConfigureSpiritualContent(user);
  const [meetings, setMeetings] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const rows = isCoach ? await fetchCoachMeetings() : await fetchSpiritualMeetings();
    setMeetings(Array.isArray(rows) ? rows : []);
  };

  useEffect(() => {
    load().catch(() => setMsg('No se pudieron cargar las reuniones.'));
  }, [isCoach]);

  const save = async (extra = {}) => {
    setBusy(true);
    setMsg('');
    try {
      await saveCoachMeeting({
        ...form,
        ...extra,
        capacity: form.capacity === '' ? null : Number(form.capacity),
      });
      setForm(emptyForm());
      setMsg('Reunión guardada.');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  const register = async (id) => {
    setBusy(true);
    try {
      await registerSpiritualMeeting(id);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'No se pudo inscribir.');
    } finally {
      setBusy(false);
    }
  };

  const join = async (m) => {
    setBusy(true);
    try {
      const row = await attendSpiritualMeeting(m.id);
      const url = row.join_url || m.join_url;
      if (url) window.open(url, '_blank', 'noopener');
      else setMsg('Aún no hay link de Zoom.');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'No se pudo ingresar.');
    } finally {
      setBusy(false);
    }
  };

  const edit = (m) => {
    setForm({
      id: m.id,
      title: m.title || '',
      start_time: toLocalInput(m.start_time),
      end_time: toLocalInput(m.end_time),
      zoom_link: m.join_url || '',
      recording_url: m.recording_url || '',
      capacity: m.capacity || '',
      description: m.description || '',
    });
  };

  return (
    <section className="spiritual-today-widget space-y-3">
      <h3 className="spiritual-today-widget__title">Reuniones Viento Recio</h3>
      <p className="spiritual-today-widget__body">
        {isCoach
          ? 'Programa la reunión, pega Zoom y luego el link de grabación. El CSV guarda la trazabilidad de inscritos y asistentes.'
          : 'Inscríbete para ver el Zoom. Al ingresar queda tu asistencia. Las grabaciones aparecen cuando tu entrenador las publica.'}
      </p>
      {msg ? <p className="spiritual-today-widget__body">{msg}</p> : null}

      {isCoach ? (
        <div className="spiritual-hub__card space-y-2">
          <p className="spiritual-today-widget__label">{form.id ? 'Editar reunión' : 'Nueva reunión'}</p>
          <input
            className="spiritual-hub__input"
            placeholder="Nombre de la reunión"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <label className="spiritual-today-widget__body block">
            Inicio
            <input
              className="spiritual-hub__input mt-1"
              type="datetime-local"
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            />
          </label>
          <label className="spiritual-today-widget__body block">
            Fin (opcional)
            <input
              className="spiritual-hub__input mt-1"
              type="datetime-local"
              value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            />
          </label>
          <input
            className="spiritual-hub__input"
            placeholder="Link de Zoom"
            value={form.zoom_link}
            onChange={(e) => setForm({ ...form, zoom_link: e.target.value })}
          />
          <input
            className="spiritual-hub__input"
            placeholder="Link de grabación (cuando esté lista)"
            value={form.recording_url}
            onChange={(e) => setForm({ ...form, recording_url: e.target.value })}
          />
          <input
            className="spiritual-hub__input"
            placeholder="Cupo (opcional)"
            type="number"
            min="1"
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => save({ active: true })}>
              Guardar
            </button>
            {form.id ? (
              <>
                <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={() => save({ active: false })}>
                  Ocultar
                </button>
                <button type="button" className="btn-secondary text-sm" onClick={() => setForm(emptyForm())}>
                  Nueva
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <ul className="space-y-2">
        {meetings.map((m) => (
          <li key={m.id} className="spiritual-hub__card space-y-2">
            <button type="button" className="spiritual-hub__day-toggle" onClick={() => setOpenId(openId === m.id ? null : m.id)}>
              {m.title} · {formatWhen(m.start_time)}
              {m.active === false ? ' · oculta' : ''}
              {m.live ? ' · en curso' : ''}
            </button>
            <p className="spiritual-today-widget__meta">
              {m.registered_count} inscritos
              {m.capacity ? ` / ${m.capacity} cupo` : ''}
              {m.registered ? ' · estás inscrito' : ''}
            </p>
            {openId === m.id ? (
              <div className="space-y-2">
                <p className="spiritual-today-widget__label">Inscritos</p>
                <ul className="spiritual-today-widget__body space-y-1">
                  {(m.roster || []).map((p) => (
                    <li key={p.user_id}>
                      {p.name}
                      {p.attended ? ' · asistió' : ''}
                    </li>
                  ))}
                  {!(m.roster || []).length ? <li>Nadie inscrito aún.</li> : null}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {!isCoach && !m.registered && m.upcoming && m.active !== false ? (
                    <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => register(m.id)}>
                      Inscribirme
                    </button>
                  ) : null}
                  {!isCoach && m.registered && (m.upcoming || m.live) && m.active !== false ? (
                    <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => join(m)}>
                      Ingresar
                    </button>
                  ) : null}
                  {isCoach ? (
                    <>
                      <button type="button" className="btn-secondary text-sm" onClick={() => edit(m)}>Editar</button>
                      <button type="button" className="btn-secondary text-sm" onClick={() => downloadMeetingAttendanceCsv(m.id)}>
                        CSV asistencia
                      </button>
                    </>
                  ) : null}
                  {m.recording_url ? (
                    <a className="btn-secondary text-sm" href={m.recording_url} target="_blank" rel="noopener noreferrer">
                      Ver grabación
                    </a>
                  ) : null}
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {!meetings.length ? (
        <p className="spiritual-today-widget__body">Aún no hay reuniones programadas.</p>
      ) : null}
    </section>
  );
}
